<template>
  <div class="row justify-content-center mobile-fix-margin">
    <div class="col mobile-fix-padding">

      <!-- Bayarcash error -->
      <div class="alert alert-danger" v-if="hasBcConsoleErrors && ! form.processing">
        <p class="m-0">FPX server error. Please retry again.</p>
      </div>

      <!-- Alert -->
      <div class="alert alert-danger" v-if="Object.keys(form.errors).length && ! form.processing">
        <ul class="mb-0">
          <template v-for="err_key in form.errors">
            <li>{{ err_key }}</li>
          </template>
        </ul>
      </div>

      <!-- Total -->
      <div v-show="! form.processing" id="payment-form-total-donation"
           class="border p-4 text-center bg-primary text-white mb-4 rounded">
        <h1>
          {{ $t('donation-form.total_donation') }}
        </h1>
        <h2 class="mb-0">
          {{ vueNumberFormat(total) }}
        </h2>

        <!-- Payment gateway fees -->
        <h6 class="mt-2 mb-0 text-14 font-weight-normal" v-if="formData.payment_gateway_fees_enabled">
          {{ $t('donation-form.payment_gateway_fees').replace('#amount#', form.payment_gateway_fees_display) }}
        </h6>
      </div>

      <!-- Form -->
      <form v-show="! form.processing" method="POST" class="p-1 payment-form">

        <input type="hidden" name="form.order_amount">

        <!-- Donor Details -->
        <div>
          <donor-details
              v-model:participant_name="form.participant_name"
              v-model:participant_id_type="form.participant_id_type"
              v-model:participant_id="form.participant_id"
              v-model:participant_tel_no="form.participant_tel_no"
              v-model:participant_email="form.participant_email"
              v-model:mandate_period="form.mandate_period"
              :mandate_enabled="props.formData.mandate_enabled"
              :mandate_application_type="props.formData.mandate_application_type"
              :mandate_period_enabled="props.formData.mandate_period_enabled"
              :mandate_period_options_data="props.formData.mandate_period_options_data"
              :recurring_transaction_enabled="props.formData.recurring_transaction_enabled"
              :participant_email_required="props.formData.participant_email_required"
              :id_types="formData.id_type"
              :errors="form.errors"
              @random-fill="fillRandomAddress"
          />

          <!-- Address -->
          <donor-address
              ref="donorAddressRef"
              v-model:participant_address1="form.participant_address1"
              v-model:participant_address2="form.participant_address2"
              v-model:participant_postcode="form.participant_postcode"
              v-model:participant_city="form.participant_city"
              v-model:participant_state="form.participant_state"
              v-model:participant_country="form.participant_country"
              :participant_address_field_enabled="props.formData.participant_address_field_enabled"
              :participant_email_required="props.formData.participant_email_required"
              :participant_address_field_required="props.formData.participant_address_field_required"
              :mandate_enabled="props.formData.mandate_enabled"
              :mandate_application_type="props.formData.mandate_application_type"
              :city_opts="props.cityOpts"
              :state_opts="props.stateOpts"
              :country_opts="props.countryOpts"
              :default_country="props.formData.default_country"
              :errors="form.errors"
          />

          <!-- Custom Field -->
          <custom-fields
              :custom_fields="formData.custom_fields"
              v-model:custom_field_1_data_custom_enabled="form.custom_field_1_data_custom_enabled"
              v-model:custom_field_1_required="form.custom_field_1_required"
              v-model:custom_field_1_label="form.custom_field_1_label"
              v-model:custom_field_1_data="form.custom_field_1_data"
              v-model:custom_field_1_last_data="form.custom_field_1_last_data"
              v-model:custom_field_1_value="form.custom_field_1_value"
              v-model:custom_field_1_custom="form.custom_field_1_custom"
              v-model:custom_field_2_data_custom_enabled="form.custom_field_2_data_custom_enabled"
              v-model:custom_field_2_required="form.custom_field_2_required"
              v-model:custom_field_2_label="form.custom_field_2_label"
              v-model:custom_field_2_data="form.custom_field_2_data"
              v-model:custom_field_2_last_data="form.custom_field_2_last_data"
              v-model:custom_field_2_value="form.custom_field_2_value"
              v-model:custom_field_2_custom="form.custom_field_2_custom"
              v-model:custom_field_3_data_custom_enabled="form.custom_field_3_data_custom_enabled"
              v-model:custom_field_3_required="form.custom_field_3_required"
              v-model:custom_field_3_label="form.custom_field_3_label"
              v-model:custom_field_3_data="form.custom_field_3_data"
              v-model:custom_field_3_last_data="form.custom_field_3_last_data"
              v-model:custom_field_3_value="form.custom_field_3_value"
              v-model:custom_field_3_custom="form.custom_field_3_custom"
              :errors="form.errors"
          />
        </div>

        <!-- Postage Zone -->
        <postage-zone
            v-model:postage_zone="form.postage_zone"
            v-model:postage_amount="form.postage_amount"
            :postage_enabled="props.formData.postage_enabled"
            :postage_zones="props.formData.postage_zones"
            :mandate_enabled="props.formData.mandate_enabled"
            :qty="totalQty"
            :errors="form.errors"
        />

        <!-- Tax Exemption -->
        <tax-details
            v-model:exclude_tax="form.exclude_tax"
            v-model:tax_ic_no="form.tax_ic_no"
            v-model:tax_address1="form.tax_address1"
            v-model:tax_address2="form.tax_address2"
            v-model:tax_postcode="form.tax_postcode"
            v-model:tax_city="form.tax_city"
            v-model:tax_state="form.tax_state"
            v-model:tax_country="form.tax_country"
            :tenant_can_issue_tax_receipt="props.formData.tenant_can_issue_tax_receipt"
            :city_opts="props.cityOpts"
            :state_opts="props.stateOpts"
            :country_opts="props.countryOpts"
            :default_country="props.formData.default_country"
            :errors="form.errors"
        />

        <!-- Payment Methods -->
        <div class="border-top pt-4 mt-3">
          <payment-methods
              v-model:payment_gateway.number="form.payment_gateway"
              v-model:merchant_bank_account="form.merchant_bank_account"
              v-model:bank_transfer_type="form.bank_transfer_type"
              v-model:bank_transfer_date="form.bank_transfer_date"
              v-model:bank_transfer_notes="form.bank_transfer_notes"
              v-model:proof_of_payment="form.proof_of_payment"
              v-model:fpx_buyer_bank="form.fpx_buyer_bank"
              v-model:fpx_buyer_bank_code="form.fpx_buyer_bank_code"
              v-model:fpx_buyer_bank_name="form.fpx_buyer_bank_name"
              :mandate_enabled="formData.mandate_enabled"
              :payment_channels="formData.payment_channels"
              :paymentGateways="paymentGateways"
              :fpx_gateway_code="formData.fpx_gateway_code"
              :tenant="tenant"
              :form_code="props.formData.code"
              :manual_bank_transfer_enabled="formData.manual_bank_transfer_enabled"
              :manual_bank_transfer_gateway_code="formData.manual_bank_transfer_gateway_code"
              :manual_bank_transfer_payment_notes="formData.manual_bank_transfer_payment_notes"
              :bankAccounts="bankAccounts"
              :fpxBanksList="fpxBanksList"
              :fpxBanksLogo="fpxBanksLogo"
              :demo_mode="demo_mode"
              :total="total"
              :processing="form.processing"
              :errors="form.errors"
              :step-number="4"
          />
        </div>

        <!-- Mandate Frequency Information -->
        <div v-if="formData.mandate_enabled && total > 0">
          <div class="card rounded">
            <div class="card-body rounded" style="background-color: #f8f9fa;">
              <div style="display: flex; justify-content: space-between; align-items: center; width: 100%;">
                <h5 class="mb-0 fw-bold">
                  <span v-if="form.mandate_frequency_mode === 'WK'">{{ $t('donation-form.total') }} {{ $t('donation-form.recurring_frequency_weekly') }}:</span>
                  <span v-else>{{ $t('donation-form.total') }} {{ $t('donation-form.recurring_frequency_monthly') }}:</span>
                </h5>
                <h5 class="mb-0 fw-bold text-success">RM {{ Number(mandateTotal).toFixed(2) }}</h5>
              </div>

              <div style="display: flex; align-items: center;">
                <hr class="flex-grow-1 mx-auto my-2" style="width: 50%; border-top: 1px solid #dee2e6;">
              </div>

              <p class="text-muted mb-0 text-start" style="font-size: 0.875rem;">
                <i class="fas fa-info-circle align-center"></i>
                {{$t('donation-form.mandate_info')}}
                <span v-if="form.mandate_frequency_mode === 'WK'" class="fw-medium text-success fw-bold" style="font-size: 1.1em; border-bottom: 1px solid #28a745;">{{ $t('donation-form.mandate_weekly') }}</span>
                <span v-else class="fw-medium text-success fw-bold" style="font-size: 1.1em; border-bottom: 1px solid #28a745;">{{ $t('donation-form.mandate_monthly') }}</span>.
              </p>
            </div>
          </div>
        </div>

        <!-- Final Step -->
        <div class="border-top pt-4">

          <!-- Consent -->
          <template v-if="formData.participant_marketing_consent_enabled == 1">
            <div class="mb-2">
              <div class="alert alert-warning">
                <div class="custom-control custom-checkbox pl-3" value="1">
                  <input type="checkbox" class="custom-control-input" id="checkbox-participant_sms_email_marketing_consent" value="true" v-model="form.participant_sms_email_marketing_consent">
                  <label class="custom-control-label mb-0" for="checkbox-participant_sms_email_marketing_consent">
                    {{ $t('donation-form.sms_email_marketing_consent') }}
                  </label>
                </div>
              </div>
            </div>
          </template>

          <!-- Custom Consent -->
          <template v-if="formData.custom_consent_enabled == 1">
            <div class="mb-2">
              <div class="alert alert-warning">
                <div class="custom-control custom-checkbox pl-3" value="1">
                  <input type="checkbox" class="custom-control-input" id="checkbox-participant_custom_consent" value="true" v-model="form.participant_custom_consent">
                  <label class="custom-control-label mb-0" for="checkbox-participant_custom_consent" v-html="formData.custom_consent_text">
                  </label>
                </div>
              </div>
            </div>
          </template>

          <!-- Button -->
          <div class="text-center align-items-baseline justify-content-center mb-4">

            <!-- Proceed -->
            <button type="button" class="btn btn-lg btn-success btn-block-modified mb-4" @click="submit">
              <template v-if="formData.mandate_enabled && formData.mandate_application_type === '01'">
                {{ $t('donation-form.proceed_to_emandate_enrollment') }}
              </template>
              <template v-else-if="formData.mandate_enabled && formData.mandate_application_type === '02'">
                {{ $t('donation-form.proceed_to_emandate_maintenance') }}
              </template>
              <template v-else-if="formData.mandate_enabled && formData.mandate_application_type === '03'">
                {{ $t('donation-form.proceed_to_emandate_termination') }}
              </template>
              <template v-else>
                {{ $t('donation-form.proceed_to_donation') }}
              </template>

              <i class="fad fa-angle-double-right ml-1"></i>
            </button>

            <!-- Back -->
            <a class="text-15" @click="$emit('prev')">
              <i class="fad fa-angle-double-left mr-1"></i>
              {{ $t('donation-form.back') }}
            </a>
          </div>

          <!-- Recurring Notice -->
          <template v-if="formData.recurring_transaction_enabled == 1">
            <div class="pr-5 pl-5 ml-5 mr-5 mt-3 mb-4 text-center text-13 mobile-pl-0 mobile-pr-0 mobile-mr-0 mobile-ml-0" style="color:#bbb;">
              {{ formData.recurring_footer_note }}
            </div>
          </template>
        </div>
      </form>

      <!-- Redirecting to Payment Gateway -->
      <div v-show="form.processing">
        <div class="my-5 d-flex flex-column align-items-center justify-content-center">
          <h5 class="text-center">
            <template v-if="form.payment_gateway == formData.manual_bank_transfer_gateway_code">
              {{ $t('donation-form.processing_manual_bank_transfer') }}
            </template>

            <template v-else>
              {{ $t('donation-form.redirect_to_payment_gateway') }}
            </template>
          </h5>
        </div>
      </div>
    </div>
  </div>
</template>

<!-- Scripts -->
<script setup>
import DonorDetails from "@/Pages/Donations/DonorDetails.vue";
import DonorAddress from "@/Pages/Donations/DonorAddress.vue";
import TaxDetails from "@/Pages/Donations/TaxDetails.vue";
import PaymentMethods from "@/Pages/Donations/PaymentMethods.vue";
import PostageZone from "@/Pages/Donations/PostageZone.vue";

import {computed, nextTick, onMounted, ref, watch} from "vue";
import {useForm} from "@inertiajs/inertia-vue3";
import useTrackingCode from "@/Composables/UseTrackingCode";
import useHelpers from "@/Composables/UseHelpers";
import InitializeFormCustomFieldsValue from "@/Lib/InitializeFormCustomFieldsValue";
import CustomFields from "@/Pages/Donations/CustomFields.vue";

const {$t, capitalize, calculatePaymentGatewayFee} = useHelpers();
const {getFbClientId, sendFacebookPixelInitiateCheckoutEvent, sendGoogleAdsBeginCheckoutEvent} = useTrackingCode();

const props = defineProps({
	formData: Object,
	total: Number,
	participantAddressFieldEnabled: Boolean | Number,
	summary: Object,
	cityOpts: Object,
	stateOpts: Object,
	countryOpts: Object,
	bankAccounts: Object,
	fpxBanksList: Object,
	fpxBanksLogo: Object,
	paymentGateways: Object,
	demo_mode: Boolean,
	tenant: Object,
	mandate_period_opts: Array,
});

const form = useForm({
	form_id: props.formData.id,
	form_language: props.formData.language,

	// Direct Debit
	participant_name: props.formData.pre_transaction_data.participant_name ?? '',
	participant_email: props.formData.pre_transaction_data.participant_email ?? '',
	participant_tel_no: props.formData.pre_transaction_data.participant_tel_no ?? '',
	participant_id_type: props.formData.pre_transaction_data.participant_id_type ?? 1,
	participant_id: props.formData.pre_transaction_data.participant_id ?? '',
	order_amount: props.total,
	mandate_period: props.formData.pre_transaction_data.mandate_period ?? props.mandate_period,

	// Marketing consent
	participant_sms_email_marketing_consent: false,

	// Custom Consent
	participant_custom_consent_enabled: props.formData.custom_consent_enabled,
	participant_custom_consent: null,

	// Address (if enable)
	participant_email_required: props.formData.participant_email_required,
	participant_address_field_enabled: props.formData.participant_address_field_enabled,
	participant_address_field_required: props.formData.participant_address_field_required,
	participant_address1: '',
	participant_address2: '',
	participant_postcode: '',
	participant_city: '',
	participant_state: '',
	participant_country: props.formData.participant_address_field_enabled ? props.formData.default_country : '',

	// Tax exemption
	exclude_tax: false,
	tax_ic_no: '',
	tax_address1: '',
	tax_address2: '',
	tax_postcode: '',
	tax_city: '',
	tax_state: '',
	tax_country: props.formData.default_country,

	// Custom data field 1
	custom_field_1_data_custom_enabled: '',
	custom_field_1_required: '',
	custom_field_1_label: '',
	custom_field_1_data: '',
	custom_field_1_last_data: '',
	custom_field_1_value: '',
	custom_field_1_custom: '',

	// Custom data field 2
	custom_field_2_data_custom_enabled: '',
	custom_field_2_required: '',
	custom_field_2_label: '',
	custom_field_2_data: '',
	custom_field_2_last_data: '',
	custom_field_2_value: '',
	custom_field_2_custom: '',

	// Custom data field 3
	custom_field_3_data_custom_enabled: '',
	custom_field_3_required: '',
	custom_field_3_label: '',
	custom_field_3_data: '',
	custom_field_3_last_data: '',
	custom_field_3_value: '',
	custom_field_3_custom: '',

	// Payment method
	payment_gateway: props.paymentGateways[0].code,
	custom_fpx_payment_flow_enabled: Boolean(props.tenant.custom_fpx_payment_flow_enabled),
	fpx_buyer_bank: '',
	fpx_buyer_bank_code: null,
	fpx_buyer_bank_name: null,
	merchant_bank_account: props.bankAccounts[0],
	bank_transfer_type: 'Internet Banking',
	bank_transfer_date: new Date().toISOString().slice(0, 10),
	bank_transfer_notes: '',
	proof_of_payment: '',

	from_recurring_transaction: props.formData.from_recurring_transaction ?? false,

	payment_gateway_fees_enabled: props.formData.payment_gateway_fees_enabled ?? false,
	payment_gateway_fees: props.formData.payment_gateway_fees ?? 0,
	payment_gateway_fees_display: 0,

	// Postage
	postage_enabled: props.formData.postage_enabled ?? false,
	postage_zone: null,
	postage_amount: 0,

	// Direct Debit
	mandate_enabled: props.formData.mandate_enabled ?? false,
	mandate_application_type: props.formData.mandate_application_type,
	mandate_ref_no: props.formData.mandate_ref_no,
	mandate_frequency_mode: props.formData.mandate_frequency_mode ?? '',

	summary: props.summary,
});

onMounted(() => {
	InitializeFormCustomFieldsValue(props, form);

	updateFee(props.paymentGateways[0].code);

	// Scroll to top when in payment section
	document.querySelector('#landing-page-form').scrollIntoView({
	behavior: 'smooth'
	})

	// Set _fbp && _fbc cookie value
	form.summary._fbp = getFbClientId();
});

watch(
    () => form.payment_gateway,
    (newValue) => updateFee(newValue)
)

const hasBcConsoleErrors = computed(() => {

	// Return true
	if (form.hasErrors) {
		let rg = /^bc_console\.[a-z_]/;

		let errors = Object.keys(form.errors).filter((key) => {
			return rg.test(key);
		});

		return errors.length;
	} else {
		return false;
	}
});

const totalQty = computed(() => {
	const sum = Object.values(props.summary).filter(Array.isArray).flat().reduce((sum, item) => {
		if (item && item.count > 0 && item.amount > 0) return sum + item.count;
		return sum;
	}, 0);
	return Math.max(1, sum);
});

const mandateTotal = computed(() => {
	return Number(props.total) + Number(form.payment_gateway_fees || 0);
});

const donorAddressRef = ref(null);

function fillRandomAddress() {
	if (donorAddressRef.value) {
		donorAddressRef.value.fillRandomAddress();
	}
}

const bcConsoleErrorKeys = computed(() => {
	let rg = /^bc_console\.[a-z_]/;

	let errors = Object.keys(form.errors).filter((key) => {
		return rg.test(key)
	});

	return errors;
});

const updateFee = (newValue) => {
	if ( ! form.payment_gateway_fees_enabled) return;
	const gateway = props.paymentGateways.find((g) => g.code === newValue);

	if (gateway) {
		let paymentGatewayFee = calculatePaymentGatewayFee(gateway, form.order_amount);

		form.payment_gateway_fees = paymentGatewayFee.value;
		form.payment_gateway_fees_display = paymentGatewayFee.display;
	} else {
		form.payment_gateway_fees = '0.00';
		form.payment_gateway_fees_display = 'RM0.00';
	}
};

function hash(inputString) {
	let md = forge.md.sha256.create();
	return md.update(inputString).digest().toHex();
}

function submit() {
	let url = props.formData.submit_url;

	form.clearErrors()

	if (form.payment_gateway !== props.formData.manual_bank_transfer_gateway_code) {
		form.bank_transfer_type = null;
		form.bank_transfer_date = null;
		form.merchant_bank_account = null;
		form.bank_transfer_type = null;
	}

	if (form.payment_gateway === props.formData.manual_bank_transfer_gateway_code) {
		form.fpx_buyer_bank_code = null;
		form.fpx_buyer_bank_name = null;
	}

	form.post(url, {
		preserveScroll: true,
		preserveState: true,

		onSuccess: () => new Promise(function (resolve) {
			if (!form.hasErrors && props.formData.facebook_tracking.facebook_pixel_id) {
				sendFacebookPixelInitiateCheckoutEvent(form, props.formData, props.summary, props.formData.module);
			}

			let hasGoogleAdsBeginCheckoutConfig = props.formData.google_ads_tracking.google_ads_id && props.formData.google_ads_tracking.begin_checkout_conversion_label;

			if (!form.hasErrors && hasGoogleAdsBeginCheckoutConfig) {
				sendGoogleAdsBeginCheckoutEvent(form, props.formData, props.summary, props.formData.module);
			}

			return resolve();
		}),

		onError: function (e) {
			nextTick(() =>
				document.getElementById('landing-page-form').scrollIntoView({
					behavior: 'smooth'
				})
			);
		},
	})
}
</script>

<style>
.select2-container .select2-selection--single .select2-selection__rendered {
  margin-top: 1px;
}
</style>
